import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';
import { notification } from 'antd';
import { EntityScopes, GetMembersResponse, GetOrganizationDetailsResponse, UpdateOrganizationDetailsRequest } from '@kemu-io/kemu-types';
import organizationApi from '@src/api/organization/organizationApi';
import useTranslation from '@hooks/useTranslation';

type AccountMenuContextType = {
  // Members data and state
  members: GetMembersResponse | null;
  loadingMembers: boolean;
  memberScreenFormValues: MemberScreenFormValues | null;
  handleUpdateMemberScreenFormValues: (values: MemberScreenFormValues) => void;
  setTempMemberDetails: (details: NewMemberDetails | null) => void;
  getTempMemberDetails: () => NewMemberDetails | null;
  editingMemberId: string | null;
  setEditingMemberId: (id: string | null) => void;

  // Organization details data and state
  organizationDetails: GetOrganizationDetailsResponse | null;
  loadingOrganizationDetails: boolean;
  focusedField: string | null;
  getOrganizationDetailsFormValues: () => GetOrganizationDetailsResponse | null;
  setOrganizationDetailsFormValues: (values: GetOrganizationDetailsResponse) => void;
  setFocusedField: (field: string) => void;
  // handleUpdateOrganizationDetailsFormValues: (values: GetOrganizationDetailsResponse) => void;

  // Methods
  handleFetchMembers: () => Promise<void>;
  handleFetchOrganizationDetails: () => Promise<void>;
  handleSaveOrgDetails: (values: UpdateOrganizationDetailsRequest) => Promise<void>;

};

const AccountMenuContext = createContext<AccountMenuContextType | undefined>(undefined);

type AccountMenuProviderProps = {
  children: ReactNode;
};

export type NewMemberDetails = {
  id?: string;
  firstName: string;
  lastName: string;
  role: 'Owner' | 'Admin' | 'Member';
  groups: string[];
  entityScopes?: EntityScopes;
  status: 'active' | 'inactive';
  createdAt: string;
  email?: string;
  loginId?: string;
  lastSeenAt?: string;
};

export type MemberScreenFormValues = {
  searchText: string;
  statusFilter: string;
  groupFilter: string;
  currentPage: number;
  pageSize: number;
  remainingSeats: number;
  editingMember: NewMemberDetails | null;
};

/**
 * Provider component that manages account menu state and operations
 * Centralizes data fetching and state management for organization members and details
 */
export const AccountMenuProvider: React.FC<AccountMenuProviderProps> = ({ children }) => {
  const t = useTranslation('AccountMenu.Modal');
  
  // Members state
  const [members, setMembers] = useState<GetMembersResponse | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  // Organization details state
  const [organizationDetails, setOrganizationDetails] = useState<GetOrganizationDetailsResponse | null>(null);
  const [loadingOrganizationDetails, setLoadingOrganizationDetails] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [tempMemberDetails, setTempMemberDetails] = useState<NewMemberDetails | null>(null);
  // Due to the nature of Clerk sessions, the UserProfile component gets refreshed every time the session is checked, causing
  // custom pages to lose any temporary values, this is a workaround to store the form values in a separate state
  // const [organizationDetailsFormValues, setOrganizationDetailsFormValues] = useState<GetOrganizationDetailsResponse | null>(null);
  const organizationDetailsFormValuesRef = useRef<GetOrganizationDetailsResponse | null>(null);
  const [memberScreenFormValues, setMemberScreenFormValues] = useState<MemberScreenFormValues | null>(null);

  const handleUpdateMemberScreenFormValues = useCallback((values: MemberScreenFormValues) => {
    setMemberScreenFormValues(values);
  }, []);

  const setOrganizationDetailsFormValues = useCallback((values: GetOrganizationDetailsResponse) => {
    // setOrganizationDetailsFormValues(values);
    organizationDetailsFormValuesRef.current = values;
  }, []);


  const getOrganizationDetailsFormValues = useCallback(() => {
    return organizationDetailsFormValuesRef.current;
  }, []);

  /**
   * Fetches the list of organization members from the API
   */
  const handleFetchMembers = useCallback(async (): Promise<void> => {
    setLoadingMembers(true);
    try {
      const response = await organizationApi.getMembers();
      setMemberScreenFormValues({
        searchText: '',
        statusFilter: 'all',
        groupFilter: 'all',
        currentPage: 1,
        pageSize: response.members.length,
        editingMember: null,
        remainingSeats: response.remainingSeats,
      });
      setMembers(response);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      notification.error({
        message: t.withBaseKey('CommonWords')('Error'),
        description: t('UserManagement.Actions.Error.FetchFailed'),
      });
    } finally {
      setLoadingMembers(false);
    }
  }, [t]);

  /**
   * Fetches the organization details from the API
   */
  const handleFetchOrganizationDetails = useCallback(async (): Promise<void> => {
    setLoadingOrganizationDetails(true);
    try {
      const res = await organizationApi.getOrganizationDetails();
      
      const values: GetOrganizationDetailsResponse = {
        name: res.name,
        location: res.location ? {
          country: res.location.country || '',
          city: res.location.city || '',
          address1: res.location.address1 || '',
          address2: res.location.address2 || '',
          postCode: res.location.postCode || '',
        } : {
          country: '',
          city: '',
          address1: '',
          address2: '',
          postCode: '',
        }
      };
      setOrganizationDetailsFormValues(values);
      setOrganizationDetails(values);
    } finally {
      setLoadingOrganizationDetails(false);
    }
  }, []);

  const getTempMemberDetails = useCallback(() => {
    return tempMemberDetails;
  }, [tempMemberDetails]);

  /**
   * Saves the organization details to the API
   */
  const handleSaveOrgDetails = useCallback(async (values: UpdateOrganizationDetailsRequest): Promise<void> => {
    try {
      const updatedValues = await organizationApi.updateOrganizationDetails(values);
      setOrganizationDetails(updatedValues);
    } catch(e){
      console.error(e);
      notification.error({
        message: t.withBaseKey('CommonWords')('Error'),
        description: t('UserManagement.Actions.Error.OrganizationDetailsSaveFailed'),
      });
    }
  }, [t]);

  const contextValue: AccountMenuContextType = {
    members,
    loadingMembers,
    organizationDetails,
    loadingOrganizationDetails,
    focusedField,
    setFocusedField,
    handleFetchMembers,
    handleFetchOrganizationDetails,
    handleSaveOrgDetails,
    handleUpdateMemberScreenFormValues,
    setOrganizationDetailsFormValues,
    getOrganizationDetailsFormValues,
    memberScreenFormValues,
    editingMemberId,
    setEditingMemberId,
    setTempMemberDetails,
    getTempMemberDetails,
  };

  return (
    <AccountMenuContext.Provider value={contextValue}>
      {children}
    </AccountMenuContext.Provider>
  );
};

/**
 * Hook to access the AccountMenu context
 * @returns AccountMenuContextType
 * @throws Error if used outside of AccountMenuProvider
 */
export const useAccountMenuContext = (): AccountMenuContextType => {
  const context = useContext(AccountMenuContext);
  if (context === undefined) {
    throw new Error('useAccountMenu must be used within an AccountMenuProvider');
  }
  return context;
};
